import React from 'react';
import RecipeBookRow from './RecipeBookRow';
import { SHOW_UNIT_COLUMN, rowKeyOf } from './recipeBookStore';

/**
 * One dish card, laid out the way the printed book lays it out (spec §12.2):
 *
 *   1. Chocolate                                       Rs 100.00
 *   RAW INGREDIENTS
 *   S.No | INGREDIENT | QTY
 *   ...
 *   SAVED PREPARATIONS
 *   ...
 *
 * The complimentary rows sit at the end of RAW INGREDIENTS with the "(Comp)" suffix,
 * exactly where the PDF puts them, because the book is what is being proofread.
 *
 * The delete warnings are the spec's, §12.7, and they are computed here because only
 * this component knows how many rows the dish has left.
 */
export default function RecipeBookDish({ dish, index, store }) {
  const { pendingChanges, pendingDeletes, conflicts, setValue, toggleDelete, keepMine, takeTheirs } = store;

  const rawRows = [...dish.raw_ingredients, ...dish.complimentary];
  const prepRows = dish.preparations;

  // How many dish rows survive this batch — drives the "will have no recipe" warning.
  const stagedInDish = [...rawRows, ...prepRows].filter((r) => pendingDeletes.has(rowKeyOf(r))).length;
  const totalDishRows = rawRows.length + prepRows.length;
  const lastOneGoing = totalDishRows > 0 && stagedInDish >= totalDishRows;

  const warningFor = (row) => {
    if (row.scope === 'prep_in_dish') {
      return 'removes this dish’s usage only — the preparation itself is kept';
    }
    if (row.scope === 'comp_in_dish') {
      const others = (row.shared_with_item_count || 1) - 1;
      return others > 0
        ? `used by ${others} other item${others === 1 ? '' : 's'} — they are not affected`
        : 'removed from this dish only';
    }
    if (lastOneGoing) {
      return `${dish.name} will have no recipe and won’t deduct stock`;
    }
    return null;
  };

  const renderRow = (row, serial) => {
    const key = rowKeyOf(row);
    return (
      <RecipeBookRow
        key={key}
        row={row}
        serial={serial}
        pendingChange={pendingChanges.get(key)}
        pendingDelete={pendingDeletes.get(key)}
        conflict={conflicts.get(key)}
        onChange={setValue}
        onToggleDelete={toggleDelete}
        onKeepMine={keepMine}
        onTakeTheirs={takeTheirs}
        deleteWarning={warningFor(row)}
      />
    );
  };

  const colSpan = SHOW_UNIT_COLUMN ? 5 : 4;

  return (
    <article className="rb-dish" id={`rb-dish-${dish.menu_item_id}`}>
      <header className="rb-dish-head">
        <h3>
          <span className="rb-dish-index">{index}.</span> {dish.name}
        </h3>
        <span className="rb-price">Rs {Number(dish.price || 0).toFixed(2)}</span>
      </header>

      <table className="rb-table">
        <thead>
          <tr>
            <th className="rb-sno">S.No</th>
            <th className="rb-name">INGREDIENT</th>
            <th className="rb-qty">QTY</th>
            {SHOW_UNIT_COLUMN && <th className="rb-unit">UNIT</th>}
            <th className="rb-actions" aria-label="Remove" />
          </tr>
        </thead>
        <tbody>
          <tr className="rb-section-row">
            <td colSpan={colSpan}>RAW INGREDIENTS</td>
          </tr>

          {rawRows.length === 0 ? (
            <tr className="rb-empty-row">
              <td colSpan={colSpan}>No raw ingredients saved</td>
            </tr>
          ) : (
            rawRows.map((row, i) => renderRow(row, i + 1))
          )}

          {prepRows.length > 0 && (
            <>
              <tr className="rb-section-row">
                <td colSpan={colSpan}>SAVED PREPARATIONS</td>
              </tr>
              {prepRows.map((row, i) => renderRow(row, i + 1))}
            </>
          )}
        </tbody>
      </table>
    </article>
  );
}
